import { AxiosError } from 'axios';
import type { ApiError } from '@oxxean/types';

/** Mensaje genérico de credenciales: nunca revela si el correo existe. */
export const GENERIC_CREDENTIALS_ERROR = 'Correo o contraseña incorrectos.';

export const GENERIC_NETWORK_ERROR =
  'No se pudo contactar al servidor. Revise su conexión e intente nuevamente.';

/** ¿El fallo es «no hay sesión»? Distinto de «no tiene permiso» (403). */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 401;
}

export interface NormalizedError {
  statusCode: number | null;
  message: string;
  code?: string;
  correlationId?: string;
}

function isApiErrorShape(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.message === 'string' || typeof candidate.statusCode === 'number';
}

/** Traduce cualquier fallo a una forma estable, sin filtrar detalles internos. */
export function normalizeError(error: unknown): NormalizedError {
  if (error instanceof AxiosError) {
    if (!error.response) {
      return { statusCode: null, message: GENERIC_NETWORK_ERROR, code: 'NETWORK' };
    }
    const data: unknown = error.response.data;
    if (isApiErrorShape(data)) {
      return {
        statusCode: error.response.status,
        message:
          typeof data.message === 'string' && data.message.length > 0
            ? data.message
            : defaultMessageFor(error.response.status),
        code: data.code,
        correlationId: data.correlationId,
      };
    }
    return { statusCode: error.response.status, message: defaultMessageFor(error.response.status) };
  }

  if (error instanceof Error) {
    return { statusCode: null, message: error.message };
  }

  return { statusCode: null, message: 'Ocurrió un error inesperado.' };
}

export function errorMessage(error: unknown): string {
  return normalizeError(error).message;
}

function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'La solicitud contiene datos inválidos.';
    case 401:
      return 'La sesión no está activa.';
    case 403:
      return 'No tiene permiso para realizar esta acción.';
    case 404:
      return 'El recurso solicitado no existe.';
    case 409:
      return 'La operación entra en conflicto con el estado actual.';
    case 422:
      return 'No fue posible procesar los datos enviados.';
    case 429:
      return 'Demasiadas solicitudes. Espere unos segundos e intente otra vez.';
    default:
      return status >= 500
        ? 'El servidor no pudo completar la operación. Intente nuevamente.'
        : 'No fue posible completar la operación.';
  }
}
